import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { manuscriptsAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ArrowLeft, Upload, FileText } from 'lucide-react';

export default function SubmitManuscript() {
  const [form, setForm] = useState({ titulo: '', abstract_es: '', abstract_en: '', palabras_clave: '', categoria: '' });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: typeof e === 'string' ? e : e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) { setError('Debes adjuntar un archivo'); return; }
    setError('');
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('titulo', form.titulo);
      fd.append('abstract_es', form.abstract_es);
      fd.append('abstract_en', form.abstract_en);
      fd.append('palabras_clave', form.palabras_clave);
      fd.append('categoria', form.categoria);
      fd.append('file', file);
      await manuscriptsAPI.create(fd);
      navigate('/panel/mis-manuscritos');
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al enviar manuscrito');
    }
    setLoading(false);
  };

  return (
    <div className="flex-1 p-4 sm:p-6 lg:p-8" data-testid="submit-manuscript-page">
      <div className="max-w-2xl">
        <button onClick={() => navigate('/panel/mis-manuscritos')} className="inline-flex items-center gap-1 text-sm font-sans text-gray-400 hover:text-slate-dark mb-6 transition-colors" data-testid="back-to-dashboard">
          <ArrowLeft className="w-4 h-4" /> Volver a mis manuscritos
        </button>

        <h1 className="text-2xl font-serif font-bold text-slate-dark mb-2">Enviar Manuscrito</h1>
        <p className="text-sm font-sans text-gray-400 mb-8">Completa el formulario para enviar tu manuscrito a revision.</p>

        <form onSubmit={handleSubmit} className="space-y-5" data-testid="manuscript-form">
          {error && <div className="p-3 text-sm font-sans bg-red-50 text-red-600 rounded-md border border-red-100" data-testid="manuscript-error">{error}</div>}

          <div className="space-y-1.5">
            <Label className="font-sans text-sm">Titulo del manuscrito *</Label>
            <Input value={form.titulo} onChange={set('titulo')} required placeholder="Titulo completo del manuscrito" data-testid="manuscript-titulo" className="font-sans" />
          </div>

          <div className="space-y-1.5">
            <Label className="font-sans text-sm">Resumen (Espanol) *</Label>
            <Textarea value={form.abstract_es} onChange={set('abstract_es')} required placeholder="Resumen del manuscrito en espanol (max 300 palabras)" rows={5} data-testid="manuscript-abstract-es" className="font-sans" />
          </div>

          <div className="space-y-1.5">
            <Label className="font-sans text-sm">Abstract (Ingles)</Label>
            <Textarea value={form.abstract_en} onChange={set('abstract_en')} placeholder="Abstract in English (optional)" rows={4} data-testid="manuscript-abstract-en" className="font-sans" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="font-sans text-sm">Palabras Clave</Label>
              <Input value={form.palabras_clave} onChange={set('palabras_clave')} placeholder="Separadas por coma" data-testid="manuscript-keywords" className="font-sans" />
            </div>
            <div className="space-y-1.5">
              <Label className="font-sans text-sm">Categoria</Label>
              <Select onValueChange={set('categoria')} data-testid="manuscript-categoria">
                <SelectTrigger className="font-sans"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Articulo Original">Articulo Original</SelectItem>
                  <SelectItem value="Revision Sistematica">Revision Sistematica</SelectItem>
                  <SelectItem value="Caso Clinico">Caso Clinico</SelectItem>
                  <SelectItem value="Ensayo">Ensayo</SelectItem>
                  <SelectItem value="Carta al Editor">Carta al Editor</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="font-sans text-sm">Archivo (PDF o Word) *</Label>
            <div className="border-2 border-dashed border-gray-200 rounded-md p-6 text-center hover:border-trans-blue/50 transition-colors">
              <input
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={e => setFile(e.target.files[0])}
                className="hidden"
                id="file-upload"
                data-testid="manuscript-file-input"
              />
              <label htmlFor="file-upload" className="cursor-pointer">
                {file ? (
                  <div className="flex items-center justify-center gap-2">
                    <FileText className="w-5 h-5 text-trans-blue" strokeWidth={1.5} />
                    <span className="text-sm font-sans text-slate-dark">{file.name}</span>
                  </div>
                ) : (
                  <div>
                    <Upload className="w-8 h-8 text-gray-300 mx-auto mb-2" strokeWidth={1} />
                    <p className="text-sm font-sans text-gray-400">Haz clic para seleccionar archivo</p>
                    <p className="text-xs font-sans text-gray-300 mt-1">PDF o Word, max 50MB</p>
                  </div>
                )}
              </label>
            </div>
          </div>

          <Button type="submit" className="w-full bg-slate-dark hover:bg-slate-dark/90 font-sans" disabled={loading} data-testid="manuscript-submit-btn">
            {loading ? 'Enviando...' : 'Enviar Manuscrito'}
          </Button>
        </form>
      </div>
    </div>
  );
}
